import {
  Collapse,
  Divider,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Popover,
  PopoverContent,
  PopoverTrigger,
  useDisclosure,
} from '@chakra-ui/react';
import { MenuItem } from '@components/menu';
import { useSnackbar } from '@components/snackbar';
import {
  AddCircleIcon,
  CheckCircle,
  ChevronDownFilledIcon,
  FileIcon,
  FilesIcon,
  LockedIcon,
  ManageMembersIcon,
  ManageWorkspacesIcon,
  MoreIcon,
  SearchIcon,
  SettingsAltIcon,
} from '@sketchnote/icons';
import { Input, MoreActionMenu } from '@sketchnote/shared-components';
import cn from 'classnames';
import classNames from 'classnames';
import { Emoji } from 'emoji-mart';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React from 'react';
import { API, endpoints } from 'src/api';
import Analytics from 'src/api/services/analytics/analytics';
import {
  CreateWorkspace,
  useWorkspace,
  WorkspaceSettings,
  WorkspaceState,
} from 'src/components/workspaces';
import { useWorkspaceStore } from 'src/features/workspace/store/useWorkspaceStore';
import { useAuth } from 'src/shared/hooks/use-auth';
import { useLayoutState } from 'src/shared/hooks/use-layout';
import { shallow } from 'zustand/shallow';

import ActionArrowRight from '../../../../../icons/src/action-arrow-right.svg';
import { useSidebarStore, Workspace } from '../store';
import { UnPinFile } from './wrapper';

const { useEffect, useState, useCallback } = React;

export interface workspaceMembers {
  access: string;
  email: string;
  member: string;
  name: string;
  avatar?: string;
}

export const WorkspaceData = ({
  handleImport,
}: {
  handleImport: (workspaceId?: string) => void;
}) => {
  const { workspaces, fetchSidebar, setSelectedWorkspaceId, selectedWorkspaceId } = useSidebarStore(
    (s) => ({
      workspaces: s.workspaces,
      fetchSidebar: s.fetchSidebar,
      selectedWorkspaceId: s.selectedWorkspaceId,
      setSelectedWorkspaceId: s.setSelectedWorkspaceId,
    }),
    shallow
  );
  const { onClose: onCloseSettingsMenu } = useDisclosure();
  const [openSnackbar] = useSnackbar();
  const { workspaceModal, setWorkspaceModal } = useLayoutState();
  const [activeWorkspaceSettings, setActiveWorkspaceSettings] = useState<
    'general' | 'members' | 'workspaces'
  >('general');
  const [activeId, setActiveId] = useState<string>();
  const { setOnSearchUsers, setOnCreateWorkspace } = useWorkspace();
  const authData = useAuth((s) => s.userDetails);
  const { actions, workspace } = useWorkspaceStore();
  const router = useRouter();
  const orgId = router?.query?.orgId;
  const [showWSFiles, setShowWSFiles] = useState(true);
  const [showWSList, setShowWSList] = useState(false);
  const [searchText, setSearchText] = useState('');

  const selectedWorkspace =
    workspaces.find((ws) => ws.workspaceId === selectedWorkspaceId) ||
    (workspaces.length > 0 && workspaces[0]);

  const memberRole = authData?.memberInformation?.role;
  let access = 'view';
  if (memberRole === 'guest') {
    access = workspace?.guests?.find(
      (i) => i.member.memberId === authData?.memberInformation.memberId
    )?.access;
  } else if (memberRole === 'member' || memberRole === 'admin') {
    access = 'full';
  } else {
    const member = workspace?.members?.find(
      (i) => i.member.memberId === authData?.memberInformation.memberId
    );
    if (member) {
      access = member.access;
    } else {
      // check if member is owner
      access =
        workspace?.owner?.member.memberId === authData?.memberInformation.memberId
          ? 'full'
          : 'view';
    }
  }

  const handleSearchUsers: WorkspaceState['onSearchUsers'] = useCallback(async (search) => {
    try {
      const response = await API.get(`user/member?name=${search}`);
      if (response.data.success) {
        return response.data?.data?.map((obj) => ({
          id: obj.memberId,
          username: obj.firstname,
          email: obj.email,
          userType: obj.role,
          avatar: obj.avatar,
        }));
      }
    } catch (error) {
      console.error(error);
    }

    return [];
  }, []);
  function handleWorkspaceModal() {
    setWorkspaceModal(true, 'create');
  }

  const handleDeleteWorkspace = async (workspace: Workspace) => {
    const response = await API.delete(`/workspace?id=${workspace?.workspaceId}`);

    if (response.data.success) {
      setWorkspaceModal(false, 'settings', false);
      fetchSidebar('w');
      Analytics.track('Workspace deleted', {
        'Deleted workspace name': workspace?.name,
        'Deleted workspace id': workspace?.workspaceId,
      });

      openSnackbar({
        text: 'Workspace deleted',
        snackType: 'standard',
        position: 'bottom-center',
      });
      //TODO: Route to the next workspace on delete current workspace
      router.push(`/w/${orgId}/dashboard`, '', {
        shallow: true,
      });
    }
  };

  const handleCreateWorkspace: WorkspaceState['onCreateWorkspace'] = useCallback(
    async ({ name, privacy, members, icon, description, emails }) => {
      const payload = {
        name: name,
        isPublic: privacy === 'public',
        icon: icon,
        description: description,
        members: members,
        emails: emails,
      };
      const response = await API.post(endpoints.workspaces.creationWorkspace.url, payload);

      if (response) {
        fetchSidebar('w');
        openSnackbar({
          text: `${name} created successfully`,
          snackType: 'standard',
          position: 'bottom-center',
        });
        return response.data.success;
      }
    },
    []
  );
  const handleTogglePublicWorkspace = async (value: boolean, workspace: Workspace) => {
    const response = await API.put('/workspace', {
      workspaceId: workspace?.workspaceId,
      isPublic: value,
    });

    if (response.data) {
      fetchSidebar('w');
      Analytics.track('Workspace privacy updated', {
        'Workspace name': workspace?.name,
        'Updated privacy': value ? 'public' : 'private',
      });
      openSnackbar({
        text: 'Workspace privacy settings changed successfully',
        snackType: 'standard',
        position: 'bottom-center',
      });
      actions.fetchWorkspace(workspace?.workspaceId);
    }
  };

  const handleUpdateWorkspace = async (name, description, icon, workspaceId) => {
    const payload = {
      workspaceId: workspaceId,
      name: name,
      icon: icon,
      description: description,
    };
    const response = await API.put(endpoints.workspaces.updateWorkspace.url, payload);
    if (response.data.success) {
      fetchSidebar('w');
      if (workspaceId === selectedWorkspaceId) actions.fetchWorkspace(workspaceId);
      else actions.fetchWorkspace(selectedWorkspaceId);
      openSnackbar({
        text: `Workspace updated!`,
        snackType: 'standard',
        position: 'bottom-center',
      });
    }
  };

  useEffect(() => {
    setOnSearchUsers(handleSearchUsers);
    setOnCreateWorkspace(handleCreateWorkspace);
  }, []);

  const handleWorkspaceSettingsModal = (value = 'settings') => {
    if (value === 'viewFiles') {
      if (!selectedWorkspace) return;
      router.push(`/w/${orgId}/w/${selectedWorkspace.workspaceId}/files`);
      return;
    }
    if (value === 'importFiles') {
      selectedWorkspace && handleImport(selectedWorkspace?.workspaceId);
      return;
    } else if (value === 'settings') setActiveWorkspaceSettings('general');
    else if (value === 'manageMembers') setActiveWorkspaceSettings('members');
    else setActiveWorkspaceSettings('workspaces');
    setWorkspaceModal(true, 'settings', false);
  };

  const openWorkspace = (workspaceId: string) => {
    router.push(`/w/${orgId}/w/${workspaceId}`);
  };

  return (
    <div>
      <section
        className={cn({
          'text-gray-500 text-sm py-10 -ml-2': true,
          'border-b': workspaces.length > 0,
        })}
      >
        {workspaces.length > 0 ? (
          <div className="mb-5">
            {authData?.authentication === 'authorised' && selectedWorkspace && (
              <>
                <section
                  className={classNames({
                    'border-gray-200 ': true,
                  })}
                >
                  <Popover
                    placement="bottom-start"
                    isOpen={showWSList}
                    onClose={() => setShowWSList(false)}
                  >
                    <PopoverTrigger>
                      <div
                        className="flex items-center gap-4 justify-between text-gray-600 py-10 px-12 rounded-sm bg-gray-100 w-full cursor-pointer group"
                        style={{ width: 232 }}
                      >
                        <div className="flex items-center flex-grow min-w-0">
                          <span
                            onClick={() => setShowWSFiles((p) => !p)}
                            style={{
                              transform: showWSFiles ? 'rotate(0deg)' : 'rotate(-90deg)',
                              transition: 'all 0.5s',
                            }}
                            className={'mr-8 flex items-center'}
                          >
                            <ChevronDownFilledIcon />
                          </span>
                          <button
                            className="flex flex-grow min-w-0 gap-4 items-center"
                            onClick={() => !showWSList && setShowWSList((p) => !p)}
                          >
                            <span className="flex items-center">
                              {selectedWorkspace.icon?.type === 'image' ? (
                                <img
                                  className="w-14 h-14 object-center object-cover"
                                  src={selectedWorkspace.icon?.id}
                                />
                              ) : (
                                <Emoji
                                  emoji={
                                    selectedWorkspace.icon?.id || {
                                      id: 'file_folder',
                                    }
                                  }
                                  set="apple"
                                  size={14}
                                />
                              )}
                            </span>
                            &nbsp;
                            <span className="block text-sm font-medium text-gray-600 truncate text-left flex-grow min-w-0">
                              {selectedWorkspace.name}
                            </span>
                          </button>
                        </div>

                        <div className="flex items-center opacity-100 group-hover:opacity-100">
                          <div className="flex items-center">
                            {!selectedWorkspace.isPublic && <LockedIcon className="mr-3 text-sm" />}
                            <Link href={`/w/${orgId}/w/${selectedWorkspace.workspaceId}`}>
                              <span className="mr-8 flex items-center cursor-pointer">
                                <ActionArrowRight />
                              </span>
                            </Link>
                            <div className="flex items-center">
                              <MoreActionMenu
                                onCloseMenu={() => {
                                  onCloseSettingsMenu();
                                }}
                                onOpenMenu={() => {
                                  setShowWSList(false);
                                }}
                                menuItems={[
                                  {
                                    label: 'View all files',
                                    value: 'viewFiles',
                                    icon: <FilesIcon size={20} />,
                                  },
                                  {
                                    label: 'Settings',
                                    value: 'settings',
                                    icon: <SettingsAltIcon size={20} />,
                                  },
                                  {
                                    label: 'Manage members',
                                    value: 'manageMembers',
                                    icon: <ManageMembersIcon size={20} />,
                                  },
                                  { value: 'divider' },
                                  {
                                    label: 'Import files',
                                    value: 'importFiles',
                                    icon: <FileIcon size={20} />,
                                  },
                                ]}
                                classNames={{
                                  menuWrap: 'text-gray-600',
                                }}
                                icon={
                                  <MoreIcon
                                    wrapperClass="flex items-center"
                                    className="text-gray-600"
                                  />
                                }
                                onClickMenuItem={(e) => handleWorkspaceSettingsModal(e.value)}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </PopoverTrigger>
                    <PopoverContent
                      className={
                        'bg-white border border-gray-200 rounded-sm shadow-lg px-12 py-8 flex flex-col gap-8'
                      }
                      style={{ width: 232 }}
                    >
                      <div className="flex items-center justify-between">
                        <div>Workspace</div>
                        <Link href={`/w/${orgId}/w/${selectedWorkspace.workspaceId}`}>
                          <span className="mr-8 flex items-center cursor-pointer text-gray-600 ">
                            <ActionArrowRight />
                          </span>
                        </Link>
                      </div>
                      <div>
                        <Input
                          placeholder="Search workspace"
                          inputClassName="text-gray-600 font-regular placeholder-gray-500 h-20"
                          textSecClassName="flex-grow"
                          wrapperClassName="py-6 px-10"
                          field="search"
                          leftIcon={<SearchIcon wrapperClass="text-gray-600 flex items-center" />}
                          onChange={(e) => setSearchText(e.target.value)}
                          value={searchText}
                        />
                      </div>
                      <div className="overflow-auto" style={{ maxHeight: 230 }}>
                        {workspaces?.map((ws) => {
                          if (ws.name === 'undefined' || ws.name === 'default-workspace')
                            return null;
                          if (
                            searchText &&
                            !ws.name?.toLowerCase().includes(searchText.toLowerCase())
                          )
                            return null;
                          return (
                            <MenuItem
                              key={ws._id}
                              onClick={() => {
                                setSelectedWorkspaceId(ws.workspaceId);
                                openWorkspace(ws.workspaceId);
                                setShowWSList(false);
                              }}
                            >
                              <div className="flex w-full gap-4 items-center">
                                <span className="flex items-center">
                                  {ws.icon?.type === 'image' ? (
                                    <img
                                      className="w-14 h-14 object-center object-cover"
                                      src={ws.icon?.id}
                                    />
                                  ) : (
                                    <Emoji
                                      emoji={
                                        ws.icon?.id || {
                                          id: 'file_folder',
                                        }
                                      }
                                      set="apple"
                                      size={14}
                                    />
                                  )}
                                </span>
                                &nbsp;
                                <span className="block text-sm font-medium text-gray-600 truncate text-left flex-grow min-w-0">
                                  {ws.name}
                                </span>
                                <div className="flex-shrink-0 flex items-center gap-4">
                                  {ws.workspaceId === selectedWorkspace.workspaceId && (
                                    <CheckCircle
                                      wrapperClass="flex items-center"
                                      className="text-primary-500"
                                    />
                                  )}
                                  {!ws.isPublic && (
                                    <LockedIcon
                                      wrapperClass="flex items-center"
                                      className="text-sm text-gray-600"
                                    />
                                  )}
                                </div>
                              </div>
                            </MenuItem>
                          );
                        })}
                      </div>
                      {memberRole !== 'guest' && (
                        <div
                          onClick={handleWorkspaceModal}
                          className="flex items-center px-8 py-6 gap-8 border text-gray-600 border-gray-200 rounded-md cursor-pointer hover:bg-gray-100"
                        >
                          <AddCircleIcon />
                          Add workspace
                        </div>
                      )}
                      <Divider />
                      <MenuItem onClick={() => handleWorkspaceSettingsModal('')}>
                        <div className="flex items-center gap-8 text-gray-800">
                          <ManageWorkspacesIcon className="text-lg" />
                          Manage workspaces
                        </div>
                      </MenuItem>
                    </PopoverContent>
                  </Popover>

                  <Collapse in={showWSFiles}>
                    {selectedWorkspace.files?.length > 0 ? (
                      <div className="mt-12">
                        {selectedWorkspace.files?.map((pinnedFile) => (
                          <div
                            key={`${pinnedFile.fileId}-${pinnedFile.name}`}
                            onMouseOver={() => setActiveId(pinnedFile.fileId)}
                            onMouseLeave={() => setActiveId('')}
                            className={cn({
                              'flex text-gray-999 hover:bg-gray-100 justify-between': true,
                              'bg-blue-100': router.query.slug === pinnedFile.fileId,
                            })}
                          >
                            <Link href={`/w/${orgId}/files/${pinnedFile.fileId}`}>
                              <a
                                className="flex items-center py-6 px-8 text-gray-999"
                                onClick={() =>
                                  Analytics.track('Workspace dashboard clicked', {
                                    'Workspace name': selectedWorkspace.name,
                                  })
                                }
                              >
                                <span className="mr-4 flex items-center">
                                  {pinnedFile.icon?.type === 'image' ? (
                                    <img
                                      className="w-14 h-14 object-center object-cover"
                                      src={pinnedFile.icon?.id}
                                    />
                                  ) : pinnedFile.icon ? (
                                    <span className="flex items-center">
                                      <Emoji emoji={pinnedFile.icon} set="apple" size={16} />
                                    </span>
                                  ) : (
                                    <FileIcon className="text-primary-400 rounded-sm" />
                                  )}
                                </span>
                                <span className="block text-xs font-medium truncate w-122 text-left">
                                  {pinnedFile.name}
                                </span>
                              </a>
                            </Link>
                            {activeId === pinnedFile.fileId && (
                              <UnPinFile file={pinnedFile} type="workspace" />
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-gray-500 p-12">Pinned files appear here</div>
                    )}
                  </Collapse>
                </section>
              </>
            )}
          </div>
        ) : (
          memberRole !== 'guest' && (
            <div
              onClick={handleWorkspaceModal}
              className="flex items-center px-8 py-6 gap-8 border text-gray-600 border-gray-200 rounded-md cursor-pointer hover:bg-gray-100"
            >
              <AddCircleIcon />
              Add workspace
            </div>
          )
        )}
      </section>

      <Modal isOpen={workspaceModal.create} onClose={() => setWorkspaceModal(false, 'create')}>
        <ModalOverlay />
        <ModalContent
          style={{
            maxWidth: '46.4rem',
            borderRadius: '0.8rem',
            margin: 'auto',
            backgroundColor: 'white',
            padding: '32px',
          }}
        >
          <ModalCloseButton className="text-xs text-gray-500  m-24" />
          <ModalBody>
            <CreateWorkspace
              onClose={() => setWorkspaceModal(false, 'create')}
              totalWorkspaces={workspaces.length || 0}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
      <Modal
        isOpen={workspaceModal.settings}
        onClose={() => setWorkspaceModal(false, 'settings', false)}
      >
        <ModalOverlay />
        <ModalContent
          width="800px"
          maxWidth="auto"
          maxHeight="auto"
          margin="auto"
          borderRadius="0.8rem"
          className="static rounded-lg"
        >
          <ModalBody className="flex items-center justify-center text-left" padding="0px">
            <WorkspaceSettings
              access={access}
              handleUpdateWorkspace={handleUpdateWorkspace}
              handleToggleWorkspace={handleTogglePublicWorkspace}
              handleDeleteWorkspace={handleDeleteWorkspace}
              workspaceId={selectedWorkspace ? selectedWorkspace?.workspaceId : ''}
              selectedMenu={activeWorkspaceSettings}
              onClose={() => setWorkspaceModal(false, 'settings', false)}
              onAddWorkspace={() => setWorkspaceModal(true, 'create')}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
};
